import { environment } from '../../environments/environment';
const TENANT_PARAM = 'tenant';

export function getTenant(): any {
  const pathSegments = window.location.pathname.split('/').filter(Boolean);
  const tenantFromPath = pathSegments.find(segment => !!environment.tenants[segment]);

  if (tenantFromPath) {
    localStorage.setItem(TENANT_PARAM, tenantFromPath);
    return environment.tenants[tenantFromPath];
  }

  const tenantFromStorage = localStorage.getItem(TENANT_PARAM);
  if (tenantFromStorage && environment.tenants[tenantFromStorage]) {
    console.log('tenant',environment.tenants[tenantFromStorage])
    return environment.tenants[tenantFromStorage];
  }

  return null;
}

// Resolves the active tenant KEY (not the URL) from the path or localStorage,
// using the same matching rules as getTenant().
export function getTenantKey(): string | null {
  const pathSegments = window.location.pathname.split('/').filter(Boolean);
  const tenantFromPath = pathSegments.find(segment => !!environment.tenants[segment]);
  if (tenantFromPath) {
    return tenantFromPath;
  }

  const tenantFromStorage = localStorage.getItem(TENANT_PARAM);
  if (tenantFromStorage && environment.tenants[tenantFromStorage]) {
    return tenantFromStorage;
  }

  return null;
}

// Whether the active tenant authenticates via WS-Federation. A WS-Fed tenant
// (e.g. sem1) is authorised by the SM.Auth session cookie established during the
// angular-ui login, so its cross-origin API calls must be sent with credentials
// (withCredentials) to attach that cookie — otherwise the backend challenges the
// request and issues a federation login redirect. MAGBypass tenants
// (omlmc/omboqa) must NOT send credentials: their backends do not return
// Access-Control-Allow-Credentials, so a credentialed request is blocked by the
// browser (page fails to load). Unknown/absent → treated as non-WS-Fed.
export function currentTenantUsesWsFed(): boolean {
  const key = getTenantKey();
  if (!key) {
    return false;
  }
  const authModes = (environment as any).tenantAuthMode as { [key: string]: string } | undefined;
  return authModes?.[key] === 'wsfed';
}

// Guards against manual tampering of the tenant in the URL. Form Editor is only
// ever reached from an authenticated angular-ui session, and every legitimate entry
// carries a fresh `?token=` handoff.
//
// SM-3928: previously this only redirected when the path named a KNOWN tenant that
// differed from the stored one. An UNKNOWN segment (e.g. /formdesigner/demo) or NO
// tenant segment at all fell straight through the first check, and getTenant() then
// silently fell back to the tenant in localStorage - so a hand-edited URL loaded the
// app normally against the previous tenant's backend. The router's '**' wildcard
// route (app-routing.module.ts) renders HomeComponent for any unmatched path, which
// is why nothing else caught it either.
//
// The rule is now: the path MUST name a known tenant, and it must be the established
// one - unless a fresh handoff token is present, or the path is one of the editor's
// own tenant-less routes.
//
// NOTE: this is a client-side guard only. It stops accidental misconfiguration and
// casual URL edits; it is NOT access control. Anyone can bypass it (disable JS, call
// the APIs directly). Real enforcement requires the backend to verify the handoff
// token's signature and carry the tenant as a signed claim.
//
// Redirects to <base href> + '/Unauthorized' (the editor's OWN route). Returns false
// when it redirects so the caller can skip bootstrapping. Must run before the app
// strips the query string (see main.ts).
export function enforceTenantIntegrity(deps: {
  pathname?: string;
  search?: string;
  basePath?: string;
  redirect?: (url: string) => void;
} = {}): boolean {
  // deps is for tests only - production callers (main.ts) invoke this with no arguments.
  const pathname = deps.pathname ?? window.location.pathname;
  const search = deps.search ?? window.location.search;
  const redirect = deps.redirect ?? ((url: string) => window.location.replace(url));

  const pathSegments = pathname.split('/').filter(Boolean);
  const tenantFromPath = pathSegments.find(segment => !!environment.tenants[segment]);
  const storedTenant = localStorage.getItem(TENANT_PARAM);
  const hasHandoffToken = new URLSearchParams(search).has('token');

  // The app is NOT always served from the domain root: QA/prod use
  // <base href="/formdesigner/">, local dev uses "/". Everything below must reason
  // about the path the Angular ROUTER sees, i.e. with that prefix removed.
  //
  // Getting this wrong caused an infinite reload loop in QA: /formdesigner/Unauthorized
  // has 'formdesigner' as its first segment, so the tenant-less-route exemption never
  // matched, the guard treated it as a missing tenant, and redirected to
  // '/formdesigner/Unauthorized' - the page it was already on.
  const baseHrefAttr = deps.basePath ?? (document.querySelector('base')?.getAttribute('href') || '/');
  const base = new URL(baseHrefAttr, window.location.origin).pathname.replace(/\/+$/, '');
  const appPath = base && (pathname.toLowerCase() + '/').startsWith(base.toLowerCase() + '/')
    ? pathname.slice(base.length)
    : pathname;
  const appSegments = appPath.split('/').filter(Boolean);

  // Local/dev builds leave every entry in environment.tenants as an empty string, so
  // !!environment.tenants[segment] can never match and no tenant is ever "recognised".
  // Enforcing there would bounce every local run, so only enforce where tenants are
  // actually configured (qa/uat/prod).
  const hasConfiguredTenants = Object.values(environment.tenants || {}).some(url => !!url);
  if (!hasConfiguredTenants) {
    return true;
  }

  // A fresh handoff from angular-ui is always legitimate - it establishes the tenant.
  if (hasHandoffToken) {
    return true;
  }

  // The editor's own routes carry no tenant segment: form-builder navigates with
  // navigateByUrl('/questions'), which replaces the whole path. Allow those so an
  // in-app navigation followed by a refresh is not mistaken for tampering. Compared
  // against appSegments (base-href-relative), not the raw path.
  const TENANTLESS_APP_ROUTES = ['questions', 'unauthorized'];
  if (appSegments.length > 0 && TENANTLESS_APP_ROUTES.includes(appSegments[0].toLowerCase())) {
    return true;
  }

  const hasNoRecognisedTenant = !tenantFromPath;
  const isTenantMismatch = !!storedTenant && storedTenant !== tenantFromPath;

  if (hasNoRecognisedTenant || isTenantMismatch) {
    // Show the Form Editor's OWN Unauthorized page rather than bouncing to the portal.
    // Redirecting to environment.homePageUrl sent the user to obm-angular-ui, a different
    // app on a different host: with no session and no tenant in that URL, angular-ui's own
    // "no tenant context" rule (app.component.ts) then forwarded them to magLoginUrl - so the
    // user landed on a MAG login screen and never saw an Unauthorized page. Staying in-app
    // behaves identically in local, QA and prod and needs no portal session.
    //
    // Casing matters - the Angular route is capital-U 'Unauthorized' (app-routing.module.ts).
    // The exemption above lower-cases before comparing, so this target is allowed through.
    const target = `${base}/Unauthorized`;

    // Loop breaker: never redirect to the page we are already on. The exemption above
    // should already have caught this, but a base-href/routing mismatch must degrade to
    // "render the Unauthorized page" rather than reload the browser forever.
    if (pathname.replace(/\/+$/, '').toLowerCase() === target.toLowerCase()) {
      return true;
    }

    redirect(target);
    return false;
  }

  return true;
}
